import {
  downloadToCSV,
  TimeLineTable,
  useTimer,
  useTimeZone,
  useTranslation,
  zonedToUTC,
  zonedToUTCAdd1Day
} from '@fareye/common'
import { LANGUAGE_STRINGS } from '@fareye/configs'
import { AllIssueListing } from '@fareye/issue-resolution'
import { Flex, RenderControl } from '@fareye/ui'
import {
  camelCase,
  cancelToken,
  currenthhmmA,
  dMmm,
  getQueryParams,
  getXlsx,
  postXlsx,
  groupBy,
  HEADERS,
  isEmpty,
  post,
  snakeCase,
  serialzeAggregationsToSummaryData
} from '@fareye/utils'
import queryString from 'query-string'
import React, { useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import { mapping } from '../../../config/mapComponents'
import { getTableColumns } from '../components/Column'
import { TRN_CONSTANTS } from '@fareye/configs'
//import ETADATA from '../../../config/eta.json'

const initApiResponse = {
  data: [],
  isLoading: false,
  isError: false,
  isSuccess: true,
  message: '',
  meta: {
    count: -1,
    pageNumber: 1
  }
}
const aggs = ['in_state', 'status', 'transport_mode', 'alert_event_types']

const JourneyTable = React.memo((props: any) => {
  let {
    // data,
    match,
    timer,
    config,
    baseUrl,
    endDate,
    type = 'Journey',
    fileName,
    location,
    startDate,
    setLastUpdate,
    selectedKey,
    searchQuery,
    showDatePicker = true,
    refreshableTable = true,
    globalSearch,
    setSummaryData,
    selectedAggsFilter,
    selectedFilters,
    setSelectedFilters,
    showTimezone
  } = props
  const [ref, inView, entry] = useInView({
    /* Optional options */
    threshold: 0
  })
  // TODO : Columns to be passed only from unique prop
  const [downloadLoading, setDownloadLoading] = React.useState<boolean>(true)
  const [apiData, setApiResponse] = React.useState<any>(initApiResponse)
  const [sorting, setSortBy] = React.useState({
    sortBy: '',
    sortByDirection: true
  })
  const { t } = useTranslation()
  const source = cancelToken().source()
  const [changeDetect, setChangeDetect] = React.useState(false)
  const [pageNo, setPageNo] = React.useState(0)
  const [recordsPerPage, setRecordsPerPage] = React.useState(10)
  const query = queryString.parse(location.search)
  const [showCommentModule, setShowCommentModule] = React.useState(false)
  const [expandedRows, setExpandedRows] = React.useState<{ [key: string]: boolean }>({})
  const { style, className, variant } = config.styling
  const { state } = useTimeZone()
  const {
    links,
    apiConfig,
    downloadApiConfig,
    issueRaisedConfig,
    sortingMapping,
    showTimeLine = true
  } = config.config
  const { extendedAggKeys = [], defaultAggs = true } = apiConfig
  const { sortBy, sortByDirection } = sorting
  const searchParams = {
    ...query,
    // startDate,
    // endDate,
    pageNo: pageNo + 1,
    recordsPerPage,
    sortByDirection: sortBy ? (sortByDirection ? 'desc' : 'asc') : '',
    ...searchQuery
  }
  const subComponentConfig = config.children
  const isMultiShipment = subComponentConfig && Object.keys(subComponentConfig).some(
    (subComp: any) => subComponentConfig[subComp].component === 'MultiShipmentTimeline'
  )
  const tableLinks: any = { ...links }
  const selectedColumn = React.useMemo(() => {
    const columns =
      selectedKey === 'tabular'
        ? config.config.columnsList
        : config.config.columns
    const sortableColumns = apiData?.meta?.sortableAttributes || []

    return getTableColumns(columns, {
      t,
      links: config.config.links,
      queryParams: {}
    })
      .filter((value: any, index: number, arr: any) => {
        // removes duplicate column with same id
        return arr.findIndex((element: any) => element.id == value.id) === index
      })
      .map((item: any) => ({
        ...item,
        sortable: sortableColumns.includes(
          item.id
            .split('.')
            .map((item: string) => camelCase(item))
            .join('.')
        )
      }))
  }, [selectedKey])

  const isFirstRun = useRef(false)
  React.useEffect(() => {
    if (isFirstRun.current) {
      setPageNo(0)
      setApiResponse(initApiResponse)
      setChangeDetect(!changeDetect)
      setExpandedRows({}) // Reset expanded rows on filter/date changes
    }
    isFirstRun.current = true
  }, [startDate, endDate, globalSearch, recordsPerPage, selectedAggsFilter])

  // Reset expanded rows when page changes
  React.useEffect(() => {
    setExpandedRows({})
  }, [pageNo])

  const isDisabledForDateRange = React.useMemo(() => {
    var startTime = (startDate ? zonedToUTC(startDate) : new Date()).getTime()
    var endTime = (endDate ? zonedToUTCAdd1Day(endDate) : new Date()).getTime()
    return Boolean(
      startTime &&
        endTime &&
        (endTime - startTime) / 1000 / 60 / 60 / 24 >
          TRN_CONSTANTS.maxDownloadDays
    )
  }, [startDate, endDate])

  const isOcean = type?.value === 'container'
  const downloadLabel = isOcean ? undefined : t('Download')
  const issueLabel = isOcean ? undefined : t('Issues Raised')
  const downloadTitle = isDisabledForDateRange
    ? t(LANGUAGE_STRINGS.MAX_DOWNLOAD, {
        MAX_DOWNLOAD: TRN_CONSTANTS.maxDownloadDays
      })
    : t('Export as Excel')

  React.useEffect(() => {
    fetchData()
  }, [pageNo, changeDetect, sorting, timer])

  function generateFilterDataFromAggregation() {
    let aggsFilter: any[] = []
    Object.entries(selectedAggsFilter).forEach(([key, value]) => {
      if (selectedAggsFilter[key].length > 0) {
        aggsFilter.push([
          {
            filterKey: key,
            filterValue: selectedAggsFilter[key],
            condition: 'include',
            filterType: 'eq'
          }
        ])
      }
    })
    return aggsFilter
  }
  function generateFilterDataFromGlobalSearch(globalSearch: any[]) {
    if (isEmpty(globalSearch)) {
      return []
    } else {
      const groupedSearch = groupBy(globalSearch, ele => ele.key)
      return Object.entries(groupedSearch).map(([key, value]) => {
        return [
          {
            filterKey: key,
            filterValue: value.reduce((res = [], obj) => {
              res.push(obj.value)
              return res
            }, []),
            condition: 'include',
            filterType: 'eq'
          }
        ]
      })
    }
  }
  function generateFilterObjectForDateRange() {
    const startFilter = [
      {
        filterKey: 'start_date',
        filterValue: startDate ? zonedToUTC(startDate) : new Date(),
        condition: 'include',
        filterType: 'gte'
      }
    ]
    const endFilter = [
      {
        filterKey: 'start_date',
        filterValue: endDate ? zonedToUTCAdd1Day(endDate) : new Date(),
        condition: 'include',
        filterType: 'lt'
      }
    ]
    const { queryParams } = apiConfig
    const statusFilter = !isEmpty(queryParams)
      ? Object.keys(queryParams).map((item: string) => queryParams[item])
      : []
    const nestedStatusFilters = statusFilter.map(value => [value]);
    if (showDatePicker && !isEmpty(statusFilter)) {
      return [startFilter, endFilter, ...nestedStatusFilters]
    } else if (!showDatePicker && !isEmpty(statusFilter)) {
      return [...nestedStatusFilters]
    } else if (showDatePicker && isEmpty(statusFilter)) {
      return [startFilter, endFilter]
    } else {
      ;[]
    }
  }

  function generateSortingObj() {
    const { sortByDirection, sortBy } = sorting
    if (sortBy) {
      const snakeParams = sortBy
        .split('.')
        .map(i => snakeCase(i))
        .join('.')
      const sort = [
        { sortBy: snakeParams, sortDir: sortByDirection ? 'asc' : 'desc' }
      ]
      return { sort }
    } else if (type.value === 'container') {
      return {sort: [{sortBy: 'start_date', sortDir: 'desc'}]}
    }
    else {
      return { sort: [{ sortBy: 'exp_start_time_utc', sortDir: 'desc' }] }
    }
  }

  async function fetchData() {
    try {
      setApiResponse({
        ...apiData,
        isLoading: true
      })
      let filter = generateFilterDataFromGlobalSearch(globalSearch)
      const dateFilter: any = generateFilterObjectForDateRange()
      const aggsFilter = generateFilterDataFromAggregation()
      filter = [...filter, ...dateFilter, ...aggsFilter]
      const aggKeys = [...aggs, ...extendedAggKeys]
      const { apiURL } = apiConfig
      const {
        data: { results, meta, aggregations }
      } = await post(
        apiURL,
        {
          filter,
          perPage: recordsPerPage,
          page: pageNo + 1,
          aggs: aggKeys,
          defaultAggs: defaultAggs,
          ...generateSortingObj(),
          ...(isMultiShipment && { includeChildJourneys: true })
        },
        source.token,
        {
          headers: HEADERS()['HEADER_INFLECTION']
        }
      )
      // merge the data with previous state on the page index
      // of which data is fetched of page size assuming pageindex starts from 0
      // ex pageIndex = 3 pageSize = 10 t.splice(30, 10, ...data)
      // this implemetation covers empty array refresh of same pageIndex
      // const tempData = results
      const tempData = [...apiData.data]
      tempData.splice(pageNo * recordsPerPage, recordsPerPage, ...results)
      // tempData.splice(pageNo * recordsPerPage, recordsPerPage, ...results)
      if (results) {
        setApiResponse({
          ...apiData,
          data: tempData,
          isSuccess: true,
          meta: {
            ...meta,
            sortableAttributes: meta.sortableAttributes.map((column: string) =>
              column
                .split('.')
                .map((item: string) => camelCase(item))
                .join('.')
            )
          },
          isError: false,
          message: isEmpty(results) ? 'No Data Found...' : '',
          isLoading: false
        })
        setLastUpdate(currenthhmmA())
        setSummaryData(serialzeAggregationsToSummaryData(aggregations))
      }
    } catch (ex:any) {
      setApiResponse({
        data: [],
        isSuccess: false,
        isError: true,
        message: t('Something went wrong...'),
        isLoading: false
      })
    }
  }

  const toggleRowExpansion = (rowId: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }))
  }

  async function downloadCSV() {
    const { type = 'GET', apiURL = '', queryParams = {} } = downloadApiConfig
    setDownloadLoading(true)
    delete searchParams.pageNo
    delete searchParams.recordsPerPage
    const queryParam = getQueryParams({
      ...searchParams,
      ...queryParams,
      plant: props.plant ? props.plant : ''
    })
    let filter: any = generateFilterDataFromGlobalSearch(globalSearch)
    const dateFilter: any = generateFilterObjectForDateRange()
    const aggsFilter = generateFilterDataFromAggregation()
    filter = [...filter, ...dateFilter, ...aggsFilter]
    const { data }: any = await (type === 'GET'
      ? getXlsx(`${apiURL}?${queryParam}`, source.token)
      : postXlsx(apiURL, { filter }, source.token))
    downloadToCSV({
      data,
      fileName: fileName || `${dMmm()}`,
      type: 'text/zip',
      extension: 'zip'
    })
    setDownloadLoading(false)
  }

  return (
    <RenderControl state={apiData} variant={'xl'} t={t}>
      <Flex column>
        <div style={{ paddingTop: 10, paddingBottom: 10, ...style }}>
          <TimeLineTable
            verticalAlign={true}
            variant={selectedKey === 'tabular' ? '' : 'card'}
            className={`responsive ${
              selectedKey === 'tabular' ? 'striped' : ''
            } ${className} multi-model`}
            data={apiData.data}
            padding="4px 10px"
            size="s"
            showTotalCount={false}
            sorting={sorting}
            sortable={selectedKey === 'tabular'}
            totalElements={apiData?.meta?.count || apiData.data.length}
            columns={selectedColumn}
            downloadLabel={downloadLabel}
            downloadTitle={downloadTitle}
            downloadDisabled={isDisabledForDateRange}
            downloadCSV={isOcean ? undefined : downloadCSV}
            commentingModule={isOcean ? undefined : () => setShowCommentModule(true)}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            issueLabel={issueLabel}
            t={t}
            {...(isMultiShipment && {
              getTrProps: (row: any) => ({
                onClick: () => {
                  const rowId = row.original.id || row.index
                  toggleRowExpansion(rowId)
                },
                style: {
                  cursor: 'pointer'
                }
              })
            })}
            subComponent={
              selectedKey !== 'tabular' && showTimeLine
                ? ({ row: { original }, index }: any) => {
                    const rowId = original.id || index
                    const isExpanded = expandedRows[rowId]
                    const hasChildJourneys =
                      Array.isArray(original?.childJourneys) &&
                      original.childJourneys.length > 0
                    let shouldShowTimeline = true
                    shouldShowTimeline = isMultiShipment && hasChildJourneys && !isExpanded ? false : shouldShowTimeline;
                    return (
                      shouldShowTimeline &&
                      selectedKey !== 'tabular' &&
                      subComponentConfig &&
                      Object.keys(subComponentConfig).map((subComp: any, i: number) => {
                        const Comp: any =
                          (mapping as any)[subComponentConfig[subComp].component]
                        return (
                          <Comp
                            key={`sub-component-${i}`}
                            baseUrl={baseUrl}
                            rowData={original}
                            timelineData={original?.stops || original?.nodes || []}
                            childJourneys={original?.childJourneys}
                            links={config.config.links}
                            queryParams={query}
                            t={t}
                          />
                        )
                      })
                    )
                  }
                : undefined
            }
            onSortedChange={(ev: any) => {
              setSortBy({
                sortBy: ev[0].id,
                sortByDirection:
                  ev[0].id === sorting.sortBy
                    ? !sorting.sortByDirection
                    : !ev[0].desc
              })
            }}
            defaultPageSize={recordsPerPage}
            onPageSizeChange={(pageSize: number) => {
              setPageNo(0)
              setRecordsPerPage(pageSize)
            }}
            defaultPageIndex={pageNo}
            onPageChange={(pageIndex: number) => {
              setPageNo(pageIndex)
            }}
          />
        </div>
        <div ref={ref} />
      </Flex>
      {showCommentModule && (
        <AllIssueListing
          showDrawer={showCommentModule}
          setShowDrawer={setShowCommentModule}
          resourceId={''}
          config={issueRaisedConfig}
          resourceType={type.value === 'package' ? 'Shipment' : 'Journey'}
          baseUrl={''}
          updateIssueCount={() => {}}
          startDate={new Date(startDate)}
          endDate={new Date(endDate)}
          showTimezone={showTimezone}
          t={t}
        />
      )}
    </RenderControl>
  )
})

export const TripTableMultiModel = (props: any) => {
  const { config, maxRefreshCount, refreshInterval } = props
  const { minAutoRefreshInterval } = config?.config || {}
  const [timer] = useTimer({
    maxRefreshCount,
    refreshInterval,
    minAutoRefreshInterval
  })
  return <JourneyTable {...props} timer={timer} />
}
